import { useEffect, useState } from 'react';

/**
 * Is the tab on screen right now?
 *
 * Written as `!== 'hidden'` and never as `=== 'visible'`. Where the Page Visibility
 * API does not exist the getter yields `undefined`, and `undefined !== 'hidden'` is
 * `true` -- so an environment without the API keeps polling exactly as it does today
 * instead of going silent forever, which is the failure nobody would notice.
 */
const isDocumentVisible = (): boolean => document.visibilityState !== 'hidden';

export function usePageVisibility(): boolean {
  const [isVisible, setIsVisible] = useState<boolean>(isDocumentVisible);

  useEffect(() => {
    // Defensive only: an environment without addEventListener never reaches here in
    // practice, since React itself would already have failed.
    if (typeof document.addEventListener !== 'function') {
      return;
    }

    const handleVisibilityChange = () => setIsVisible(isDocumentVisible());

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
}
